import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { DropdownOption } from '../types';
import { useOrderStatus, useOrderUpdate, useOrderDetails } from '../hooks';

interface UpdateOrderModalProps {
  show: boolean;
  onHide: () => void;
  orderId: string | null;
  currentStatus: string | null;
  deliveryMethod: string | null;
  onOrderUpdated: () => void;
}

const UpdateOrderModal: React.FC<UpdateOrderModalProps> = React.memo(({
  show,
  onHide,
  orderId,
  currentStatus,
  deliveryMethod,
  onOrderUpdated
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [cancellationReason, setCancellationReason] = useState<string>("");
  const [otherCancelReason, setOtherCancelReason] = useState<string>("");

  const { statusList, reasonOptions, fetchStatusList, fetchCancellationReasons } = useOrderStatus();
  const { updateLoading, updateOrder } = useOrderUpdate();
  const { productIds, fetchOrderDetails } = useOrderDetails();

  // Fetch order details when modal opens
  useEffect(() => {
    if (show && orderId) {
      fetchOrderDetails(orderId).catch(console.error);
    }
  }, [show, orderId, fetchOrderDetails]);

  // Fetch status list when current status or delivery method changes
  useEffect(() => {
    if (currentStatus && deliveryMethod) {
      fetchStatusList(currentStatus, deliveryMethod);
    }
  }, [currentStatus, deliveryMethod, fetchStatusList]);

  // Handle status change
  const handleStatusChange = useCallback(async (value: string) => {
    setSelectedStatus(value);
    
    if (value === "Cancelled") {
      try {
        await fetchCancellationReasons();
      } catch (error) {
        console.error("Error fetching cancellation reasons:", error);
      }
    } else {
      setCancellationReason("");
      setOtherCancelReason("");
    }
  }, [fetchCancellationReasons]);

  // Reset form
  const resetForm = useCallback(() => {
    setSelectedStatus("");
    setCancellationReason("");
    setOtherCancelReason("");
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    resetForm();
    onHide();
  }, [resetForm, onHide]);

  // Handle order update
  const handleUpdateOrder = useCallback(async () => {
    // Validation
    if (!selectedStatus) {
      toast.error("Please select an order status");
      return;
    }

    if (!orderId) {
      toast.error("Order information is incomplete");
      return;
    }

    if (selectedStatus === "Cancelled") {
      if (!cancellationReason || (cancellationReason === "Other Reason" && !otherCancelReason)) {
        toast.error("Please provide a cancellation reason");
        return;
      }
    }

    try {
      const productIdsString = productIds.join(",");
      await updateOrder(
        orderId,
        productIdsString,
        selectedStatus,
        cancellationReason || undefined,
        otherCancelReason || undefined
      );
      
      toast.success("Order status updated successfully!");
      onOrderUpdated();
      handleClose();
    } catch (error) {
      toast.error("Failed to update the order");
      console.error("Update order error:", error);
    }
  }, [
    selectedStatus,
    orderId,
    cancellationReason,
    otherCancelReason,
    productIds,
    updateOrder,
    onOrderUpdated,
    handleClose
  ]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="cancelOrderModal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Update Order Status</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <Form>
          <Form.Group controlId="orderStatus">
            <Form.Label>Select Order Status:</Form.Label>
            <Form.Control
              as="select"
              value={selectedStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="">Select a status</option>
              {statusList.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {selectedStatus === "Cancelled" && (
            <Form.Group controlId="cancellationReason" className="mt-2">
              <Form.Label>Reason for Cancellation:</Form.Label>
              <Form.Control
                as="select"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
              >
                <option value="">Select a reason</option>
                {reasonOptions.length > 0 ? (
                  reasonOptions.map((reason, index) => (
                    <option key={index} value={reason}>
                      {reason}
                    </option>
                  ))
                ) : (
                  <option value="">No reasons available</option>
                )}
              </Form.Control>
            </Form.Group>
          )}

          {cancellationReason === "Other Reason" && (
            <Form.Group controlId="otherCancelReason" className="mt-2">
              <Form.Label>Please specify the reason:</Form.Label>
              <Form.Control
                type="text"
                value={otherCancelReason}
                onChange={(e) => setOtherCancelReason(e.target.value)}
                placeholder="Enter your reason..."
              />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <Button
          className="btnCancel"
          variant="secondary"
          onClick={handleClose}
          disabled={updateLoading}
        >
          Close
        </Button>
        <Button
          className="btnBlue"
          variant="primary"
          onClick={handleUpdateOrder}
          disabled={updateLoading}
        >
          {updateLoading ? 'Updating...' : 'Update Order'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

UpdateOrderModal.displayName = 'UpdateOrderModal';

export default UpdateOrderModal;